import { useSyncExternalStore, type MouseEvent } from 'react';
import type { ConsoleMode } from '../lib/vmConsole';

export type ConsoleOpen = { kind: 'vm' | 'vps'; id: string; name: string; mode: ConsoleMode };

/** The terminal is docked at the bottom, one at a time. Graphical consoles float in windows, one per server;
 *  the last in `wins` is on top. */
type Open = { dock: ConsoleOpen | null; wins: ConsoleOpen[] };

const KEY = 'scamp.cx';
const subs = new Set<() => void>();
let open: Open = load();

function load(): Open {
  try {
    const o = JSON.parse(sessionStorage.getItem(KEY) || '{}') as Partial<Open>;
    return { dock: o.dock ?? null, wins: Array.isArray(o.wins) ? o.wins : [] };
  } catch {
    return { dock: null, wins: [] };
  }
}

function set(next: Open) {
  open = next;
  try { sessionStorage.setItem(KEY, JSON.stringify(open)); } catch { /* not reopened after a reload */ }
  subs.forEach((f) => f());
}

export const consoleKey = (c: ConsoleOpen) => `${c.kind}:${c.id}`;

/** Opens the terminal dock, or a console window over the current page (an open one comes to the top). */
export function openConsole(c: ConsoleOpen) {
  if (c.mode === 'serial') return set({ ...open, dock: c });
  set({ ...open, wins: [...open.wins.filter((w) => consoleKey(w) !== consoleKey(c)), c] });
}

/** Brings a console window above the others. */
export function raiseConsole(key: string) {
  const w = open.wins.find((x) => consoleKey(x) === key);
  if (w && consoleKey(open.wins[open.wins.length - 1]) !== key) set({ ...open, wins: [...open.wins.filter((x) => x !== w), w] });
}

/** Closes the dock, or the console window of one server. */
export function closeConsole(key: 'dock' | string) {
  if (key === 'dock') set({ ...open, dock: null });
  else set({ ...open, wins: open.wins.filter((w) => consoleKey(w) !== key) });
}

export function useConsoles(): Open {
  return useSyncExternalStore((f) => { subs.add(f); return () => { subs.delete(f); }; }, () => open);
}

/** The console on a page of its own: what "New tab" and a Ctrl or middle click open. */
export function consolePage(c: ConsoleOpen): string {
  return `/${c.kind === 'vps' ? 'vps' : 'vms'}/${c.id}/console?mode=${c.mode}`;
}

/** For a link to consolePage(c): a plain click opens the console over the page, Ctrl, Shift or a middle click keep the new tab. */
export function consoleClick(c: ConsoleOpen) {
  return (e: MouseEvent) => {
    if (e.button !== 0 || e.ctrlKey || e.metaKey || e.shiftKey || e.altKey) return;
    e.preventDefault();
    openConsole(c);
  };
}
